import zoomLogoImage from '@/../public/_n/images/zoom-logo.png';
import getUrl from '@/utils/getUrl';

import b2bMessages from './messages';

export interface Message {
	id: string;
	defaultMessage: string;
	description: string;
}

interface B2bSubscribeProps {
	welcomeBackground?: string;
	faqLink: string;
	errorLink: string;
	logo: string | null;
	disableGradient: boolean;
	disableSocialSignup: boolean;
	successRedirectUrl?: string | null;
	messages: {
		injectPartnerName?: boolean;
		headline: Message;
		subheadline: Message | null;
		activate: Message;
		instructions?: Message;
		help?: Message;
		legal?: Message;
		legalWelcome?: Message;
		error?: Message;
		successText?: Message;
		successCTA?: Message;
		requestAccess?: Message;
		errorRequestAccess?: Message;
		errorInvalidId?: Message;
	};
	successCTAUrl?: string;
	customSSOLinks?: {
		link1: { label: Message; url: string };
		link2: { label: Message; url: string };
		link3?: { label: Message; url: string };
	};
	partnerTerms?: string;
	endDate?: string;
}

const CALM_HELP_CENTER = 'https://calm.com/help';
export const CONTACT_SUPPORT = 'https://support.calm.com/hc/en-us?solvvyProvidedByEmployer';
const JASPER_LAKE_DAY = '/_n/images/jasper-lake.webp';
const JASPER_LAKE_NIGHT = '/_n/images/b2b/backgrounds/jasper-lake-night-zoom.webp';

const singtelSubscribeBase: Omit<B2bSubscribeProps, 'messages'> = {
	welcomeBackground: JASPER_LAKE_DAY,
	faqLink: CONTACT_SUPPORT,
	errorLink: CALM_HELP_CENTER,
	logo: '/_n/images/b2b/logos/singtel-logo.webp',
	disableGradient: false,
	disableSocialSignup: false,
};

const verizonSubscribe: B2bSubscribeProps = {
	welcomeBackground: JASPER_LAKE_DAY,
	faqLink: CONTACT_SUPPORT,
	errorLink: CALM_HELP_CENTER,
	messages: {
		headline: b2bMessages.defaultTitle,
		subheadline: null,
		activate: b2bMessages.offerMessage,
	},
	logo: '/_n/images/b2b/logos/verizon-logo.webp',
	disableGradient: false,
	disableSocialSignup: false,
	successRedirectUrl: 'https://www.verizon.com/digital/nsa/secure/ui/products/ehub/manage-dashboard',
};

export const partners: Record<string, B2bSubscribeProps> = {
	kp: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: 'https://calm.com/kp-help',
		errorLink: 'https://kp.org',
		messages: {
			headline: b2bMessages.kpTitle,
			subheadline: null,
			activate: b2bMessages.activateKpMessage,
		},
		logo: '/_n/images/b2b/logos/kp.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	lincoln: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: 'https://lincoln.com',
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.activateLincolnMessage,
		},
		logo: '/_n/images/b2b/logos/lincoln.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	gympass: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: 'https://gympass.com',
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: '/_n/images/b2b/logos/gympass.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	telus: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: 'https://www.telus.com/my-account/tv/overview/optik',
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: null,
		disableGradient: false,
		disableSocialSignup: false,
	},
	el: {
		welcomeBackground: JASPER_LAKE_NIGHT,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.esteeOfferMessage,
		},
		logo: '/_n/images/b2b/logos/esteelauder.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	default: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			injectPartnerName: true,
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: null,
		disableGradient: false,
		disableSocialSignup: false,
	},
	'blue-apron': {
		welcomeBackground: '/_n/images/b2b/backgrounds/lavendar_sunset.webp',
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.blueApronOfferMessage,
		},
		logo: '/_n/images/b2b/logos/blueapron.webp',
		disableGradient: true,
		disableSocialSignup: false,
	},
	accenture: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			injectPartnerName: true,
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: null,
		disableGradient: false,
		disableSocialSignup: true,
	},
	'aristocrat-technologies': {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: null,
		disableGradient: false,
		disableSocialSignup: false,
	},
	glade: {
		welcomeBackground: '/_n/images/b2b/backgrounds/lavendar_sunset.webp',
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			injectPartnerName: true,
			headline: b2bMessages.gladeTitle,
			subheadline: b2bMessages.gladeOfferSubheadMessage,
			activate: b2bMessages.gladeOfferMessage,
		},
		logo: '/_n/images/b2b/logos/glade.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	calm: {
		welcomeBackground: JASPER_LAKE_NIGHT,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: '/_n/images/calm-logo-square.png',
		disableGradient: false,
		disableSocialSignup: false,
	},
	optus: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.optusOfferMessage,
		},
		logo: '/_n/images/b2b/logos/optus.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	logitech: {
		welcomeBackground: '/_n/images/logitech/LogitechxCalm_key_visual.webp',
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.logitechOfferMessage,
		},
		logo: '/_n/images/logitech/logitech_white.webp',
		disableGradient: true,
		disableSocialSignup: false,
	},
	fitbit: {
		welcomeBackground: '/_n/images/b2b/backgrounds/fitbit.webp',
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.fitbitOfferMessage,
		},
		logo: '/_n/images/b2b/logos/fitbit_logo_white.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	lendingclub: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: '/_n/images/b2b/logos/Lending_Club.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	orrick: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: '/_n/images/b2b/logos/Orrick.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	comcast: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.comcastActivate,
			instructions: b2bMessages.comcastInstructions,
			legal: b2bMessages.comcastLegal,
		},
		customSSOLinks: {
			link1: {
				label: b2bMessages.comcastEmployees,
				url: 'https://myapps.microsoft.com/signin/e7e88785-25e1-4942-aff3-e400e4b9d91e?tenantId=906aefe9-76a7-4f65-b82d-5ec20775d5aa&RelayState=/Comcast-NBCU/Physical-Emotional/Benefits-cont/Calm-Meditation-Sleep-Stories',
			},
			link2: {
				label: b2bMessages.comcastNBCUEmployees,
				url: 'https://core-api.bdaout.ehr.com/sso/saml/initiate?id=https%3A%2F%2Fsts.windows.net%2F4f3526f9-97d6-412d-933a-4e30a73110f4%2F&returnurl=/Comcast-NBCU/Physical-Emotional/Benefits-cont/Calm-Meditation-Sleep-Stories',
			},
			link3: {
				label: b2bMessages.comcastUOEmployees,
				url: 'https://comcastbenefitsbc4.ehr.com/account/login',
			},
		},
		logo: '/_n/images/b2b/logos/comcast.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	'nysut-member-benefits': {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			injectPartnerName: true,
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.nysutOfferMessage,
		},
		logo: null,
		disableGradient: false,
		disableSocialSignup: false,
	},
	'resume-builder': {
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			injectPartnerName: true,
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.resumeBuilderOfferMessage,
		},
		logo: null,
		disableGradient: false,
		disableSocialSignup: false,
	},
	prym: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
			instructions: b2bMessages.prymInstructions,
			help: b2bMessages.prymHelp,
			error: b2bMessages.prymError,
		},
		logo: '/_n/images/b2b/logos/prym.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	peacock: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.peacockUpdatedOfferMessage,
		},
		logo: '/_n/images/b2b/logos/peacock-logo.webp',
		disableGradient: false,
		disableSocialSignup: false,
	},
	informatica: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: null,
		disableGradient: false,
		disableSocialSignup: false,
	},
	'verizon-1y': verizonSubscribe,
	'verizon-1m': verizonSubscribe,
	gerber: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: 'https://support.calm.com/hc/en-us',
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
			successText: b2bMessages.gerberSuccessCopy,
			successCTA: b2bMessages.gerberSuccessCTA,
		},
		logo: '/_n/images/Gerber_BabyHead.png',
		disableGradient: false,
		disableSocialSignup: false,
		successCTAUrl: `${getUrl('www')}/packs/feeds/collection-mindful-parenting`,
	},
	'singtel-1m': {
		...singtelSubscribeBase,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.singtel1mActivate,
		},
	},
	'singtel-3m': {
		...singtelSubscribeBase,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.singtel3mActivate,
		},
	},
	'singtel-1y': {
		...singtelSubscribeBase,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.singtel1yActivate,
		},
	},
	zoom: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.singtel1mActivate,
		},
		logo: zoomLogoImage.src as string,
		disableGradient: false,
		disableSocialSignup: false,
	},
	'wakemed-hospital': {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			injectPartnerName: true,
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
			instructions: b2bMessages.wakemedInstructions,
			help: b2bMessages.wakemedHelp,
		},
		logo: null,
		disableGradient: false,
		disableSocialSignup: false,
	},
	fifa: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: '/_n/images/dtc-partners/fifa-footer-logo-trimmed.png',
		disableGradient: false,
		disableSocialSignup: false,
	},
	'fifa-teams': {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: '/_n/images/dtc-partners/fifa-footer-logo-trimmed.png',
		disableGradient: false,
		disableSocialSignup: false,
	},
	'fifa-volunteers': {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
		},
		logo: '/_n/images/dtc-partners/fifa-footer-logo-trimmed.png',
		disableGradient: false,
		disableSocialSignup: false,
	},
	'cox-enterprises': {
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.offerMessage,
			legalWelcome: b2bMessages.coxEnterprisesLegal,
		},
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		logo: null,
		disableGradient: false,
		disableSocialSignup: false,
	},
	ee_1y: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.eeActivate,
		},
		logo: '/_n/images/dtc-partners/EE-logo.png',
		disableGradient: false,
		disableSocialSignup: false,
	},
	ee_1m: {
		welcomeBackground: JASPER_LAKE_DAY,
		faqLink: CONTACT_SUPPORT,
		errorLink: CALM_HELP_CENTER,
		messages: {
			headline: b2bMessages.defaultTitle,
			subheadline: null,
			activate: b2bMessages.eeActivate,
		},
		logo: '/_n/images/dtc-partners/EE-logo.png',
		disableGradient: false,
		disableSocialSignup: false,
	},
} as const;
type Partner = keyof typeof partners;
export const isPartner = (value: string): value is Partner => value in partners;
